// B-139 — the pure half of Reportes' "Equipo" tab (`EquipoCierres.jsx`): the period asked, how a
// person, an hour and a duration are written, what one person's filter leaves, and the series and
// Excel sheets the page draws. The figures are the backend's (`getCierreTeamActivity`); nothing is
// recounted here that the backend counted, only regrouped (by person, region, hour).

/** The backend takes 92 days at most. */
export const MAX_DIAS = 92;

/** The period chips: everything, three ranges, a month and a range typed. "Todo" is the default. */
export const PERIODOS_EQUIPO = [
  { clave: "todo", etiqueta: "Todo" },
  { clave: "7", etiqueta: "7 días" },
  { clave: "30", etiqueta: "30 días" },
  { clave: "mes", etiqueta: "Mes" },
  { clave: "rango", etiqueta: "Rango" },
];

const DIA_MS = 86400000;
const aUTC = (fecha) => Date.UTC(Number(fecha.slice(0, 4)), Number(fecha.slice(5, 7)) - 1, Number(fecha.slice(8, 10)));
const aFecha = (ms) => new Date(ms).toISOString().slice(0, 10);
const FECHA = /^\d{4}-\d{2}-\d{2}$/;

/** The last `dias` days up to `hoy` (YYYY-MM-DD), both ends included. */
export function rangoDeDias(hoy, dias) {
  return { desde: aFecha(aUTC(hoy) - (dias - 1) * DIA_MS), hasta: hoy };
}

/** Days from `desde` to `hasta`, both included. */
export const diasEntre = (desde, hasta) => Math.round((aUTC(hasta) - aUTC(desde)) / DIA_MS) + 1;

/**
 * The range a chip asks for: `{ desde, hasta }`, `{ todo: true }` for everything, or `{ error }` when
 * it cannot be asked yet (a range half typed, upside down or longer than the backend takes). A month
 * is cut at today.
 */
export function periodoDelEquipo(clave, hoy, { mes, desde, hasta } = {}) {
  // Everything: the backend says where it starts (the client's first closing).
  if (clave === "todo") return { todo: true, desde: null, hasta: hoy };
  if (clave === "mes") {
    if (!mes) return { error: "Elige un mes." };
    const [y, m] = mes.split("-").map(Number);
    const fin = aFecha(Date.UTC(y, m, 0));
    return { desde: `${mes}-01`, hasta: fin > hoy ? hoy : fin };
  }
  if (clave === "rango") {
    if (!FECHA.test(desde || "") || !FECHA.test(hasta || "")) return { error: "Elige las dos fechas del rango." };
    if (hasta < desde) return { error: "La fecha final no puede ser antes de la inicial." };
    if (diasEntre(desde, hasta) > MAX_DIAS) return { error: `El tablero del equipo admite hasta ${MAX_DIAS} días.` };
    return { desde, hasta };
  }
  return rangoDeDias(hoy, Number(clave) || 30);
}

/** "maria.fernandez@brinks.com" -> "Maria Fernandez": the part before the @, words capitalised. */
export function nombreDePersona(correo) {
  if (!correo) return "—";
  const usuario = String(correo).split("@")[0];
  const palabras = usuario.split(/[._-]+/).filter(Boolean);
  if (!palabras.length) return String(correo);
  return palabras.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ");
}

/** An ISO instant as Bogotá's "dd-mm hh:mm" (24 h); "—" without one. */
export function horaBogota(iso) {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  // Bogotá has no daylight saving: always UTC-5.
  const d = new Date(ms - 5 * 3600000);
  const dos = (n) => String(n).padStart(2, "0");
  return `${dos(d.getUTCDate())}-${dos(d.getUTCMonth() + 1)} ${dos(d.getUTCHours())}:${dos(d.getUTCMinutes())}`;
}

/** Hours as read by a person: "35 min", "4,5 h", "2,3 días"; "—" without a value. */
export function horasLegibles(horas) {
  if (horas === null || horas === undefined || Number.isNaN(Number(horas))) return "—";
  const h = Number(horas);
  const coma = (n) => String(Math.round(n * 10) / 10).replace(".", ",");
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) return `${coma(h)} h`;
  return `${coma(h / 24)} días`;
}

/** "2026-10-21" -> "21-10". */
export const diaCorto = (dia) => `${dia.slice(8, 10)}-${dia.slice(5, 7)}`;

/** Every person who appears anywhere in the answer, the busiest first — the filter's options. */
export function personasDelEquipo(datos) {
  if (!datos) return [];
  const vistas = new Set();
  const lista = [];
  const agregar = (p) => {
    if (p && !vistas.has(p)) {
      vistas.add(p);
      lista.push(p);
    }
  };
  (datos.people || []).forEach((p) => agregar(p.persona));
  (datos.responses?.personas || []).forEach((p) => agregar(p.persona));
  (datos.hours || []).forEach((h) => agregar(h.persona));
  return lista;
}

/**
 * The headline figures, for the whole team or one person (`persona`). The queue is never a
 * person's: it is what waits today.
 */
export function resumenDelEquipo(datos, persona = null) {
  const gente = persona ? datos.people.filter((p) => p.persona === persona) : datos.people;
  const respuestas = persona ? datos.responses.personas.filter((p) => p.persona === persona) : datos.responses.personas;
  const atendidas = respuestas.reduce((s, p) => s + p.atendidas, 0);
  const promedio = atendidas ? respuestas.reduce((s, p) => s + (p.promedioHoras || 0) * p.atendidas, 0) / atendidas : null;
  const enUnDia = respuestas.reduce((s, p) => s + p.rangos.filter((r) => r.clave === "menos4h" || r.clave === "hasta24h").reduce((t, r) => t + r.n, 0), 0);
  const abiertas = (tipo) => Object.values(datos.queue.abiertas[tipo] || {}).reduce((s, n) => s + n, 0);
  return {
    atendidas,
    promedioHoras: promedio,
    pctEnUnDia: atendidas ? Math.round((enUnDia / atendidas) * 100) : null,
    acciones: gente.reduce((s, p) => s + p.acciones, 0),
    diasProcesados: gente.reduce((s, p) => s + p.dias, 0) + (persona ? 0 : datos.automatic?.dias || 0),
    porCorreo: persona ? 0 : datos.automatic?.dias || 0,
    personas: gente.length,
    descuadres: abiertas("descuadres"),
    revisiones: abiertas("revisiones"),
    faltantes: abiertas("faltantes"),
    descuadresViejos: datos.queue.abiertas.descuadres?.mas7 || 0,
  };
}

/**
 * Each day's alerts in and out, for the chart: raised by kind, attended by the team (or by one
 * person), resolved by the data. `conFaltantes` false leaves "falta información" out — on a client
 * whose day arrives late it is hundreds a day and buries everything else.
 */
export function serieDeFlujo(flow, { persona = null, conFaltantes = false } = {}) {
  return (flow || []).map((d) => ({
    dia: d.dia,
    etiqueta: diaCorto(d.dia),
    descuadres: d.descuadres,
    revisiones: d.revisiones,
    faltantes: conFaltantes ? d.faltantes : 0,
    atendidas: persona ? d.atendidasPor?.[persona] || 0 : d.atendidas,
    porDatos: d.porDatos,
  }));
}

/** The wait per day: alerts attended and their average, for the team (weighted) or one person. */
export function serieDeRespuesta(responseDays, persona = null) {
  const dias = new Map();
  (responseDays || []).filter((r) => !persona || r.persona === persona).forEach((r) => {
    const d = dias.get(r.dia) || { dia: r.dia, etiqueta: diaCorto(r.dia), atendidas: 0, horas: 0 };
    d.atendidas += r.atendidas;
    d.horas += (r.promedioHoras || 0) * r.atendidas;
    dias.set(r.dia, d);
  });
  return [...dias.values()]
    .sort((a, b) => a.dia.localeCompare(b.dia))
    .map(({ horas, ...d }) => ({ ...d, promedioHoras: d.atendidas ? Math.round((horas / d.atendidas) * 10) / 10 : null }));
}

/**
 * The chart of actions per day: one entry per day any person acted, oldest first, with one key per
 * person (the `max` busiest; the rest summed as "Otros"). Each person's `porDia` is the backend's.
 *
 * @returns {{ dias: object[], personas: { clave, nombre }[] }}
 */
export function accionesPorDia(people, max = 5) {
  const lista = people || [];
  const principales = lista.slice(0, max);
  const resto = lista.slice(max);
  const personas = principales.map((p, i) => ({ clave: `p${i}`, nombre: nombreDePersona(p.persona) }));
  if (resto.length) personas.push({ clave: "otros", nombre: "Otros" });

  const dias = new Map();
  const sumar = (dia, clave, n) => {
    if (!dias.has(dia)) dias.set(dia, { dia, etiqueta: diaCorto(dia) });
    const fila = dias.get(dia);
    fila[clave] = (fila[clave] || 0) + n;
  };
  principales.forEach((p, i) => (p.porDia || []).forEach((d) => sumar(d.dia, `p${i}`, d.acciones)));
  resto.forEach((p) => (p.porDia || []).forEach((d) => sumar(d.dia, "otros", d.acciones)));
  return { dias: [...dias.values()].sort((a, b) => a.dia.localeCompare(b.dia)), personas };
}

/** The kinds of work a person does, in the order the "por persona" chart stacks them. */
export const TIPOS_DE_TRABAJO = [
  { clave: "dias", etiqueta: "Días procesados" },
  { clave: "alertas", etiqueta: "Alertas atendidas" },
  { clave: "correcciones", etiqueta: "Correcciones" },
  { clave: "documentos", etiqueta: "Documentos" },
  { clave: "avisos", etiqueta: "Avisos a oficinas" },
  { clave: "otros", etiqueta: "Abrir, rehacer, observaciones" },
];

/** Each person as one bar of the chart, by kind of work. */
export function trabajoPorTipo(people) {
  return (people || []).map((p) => ({
    persona: p.persona,
    nombre: nombreDePersona(p.persona),
    dias: p.dias,
    alertas: p.alertas,
    correcciones: p.correcciones,
    documentos: p.documentos,
    avisos: p.avisos,
    otros: p.abiertos + p.reabiertos + p.rehechos + p.observaciones,
  }));
}

export const DIAS_DE_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
export const DIAS_COMPLETOS = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];

/** The office hours the map measures against: Monday to Friday, 7:00 to 18:00. */
export const HORARIO = { desde: 7, hasta: 18 };

const indiceDelMayor = (lista) => lista.reduce((mejor, n, i) => (n > lista[mejor] ? i : mejor), 0);

/**
 * When the team works: actions by weekday (rows, Monday first) and hour (columns, only the hours
 * anyone worked, widened to the office hours), with each day's and each hour's total, and what it
 * says — the busiest hour, the busiest day, the busiest slot and how much was done outside office
 * hours (before 7, from 18 on, or at the weekend). `max` colours the cells.
 */
export function mapaDeHoras(hours, persona = null) {
  const filas = (hours || []).filter((h) => !persona || h.persona === persona);
  const usadas = filas.map((h) => h.hora);
  const desde = Math.min(HORARIO.desde, ...usadas);
  const hasta = Math.max(HORARIO.hasta, ...usadas);
  const horas = [];
  for (let h = desde; h <= hasta; h += 1) horas.push(h);
  const celdas = DIAS_DE_SEMANA.map(() => horas.map(() => 0));
  filas.forEach((h) => {
    celdas[h.dow - 1][h.hora - desde] += h.n;
  });
  const porDia = celdas.map((fila) => fila.reduce((s, n) => s + n, 0));
  const porHora = horas.map((_, i) => celdas.reduce((s, fila) => s + fila[i], 0));
  const max = Math.max(0, ...celdas.flat());
  const total = porDia.reduce((s, n) => s + n, 0);
  let fuera = 0;
  celdas.forEach((fila, d) => fila.forEach((n, i) => {
    if (d >= 5 || horas[i] < HORARIO.desde || horas[i] >= HORARIO.hasta) fuera += n;
  }));
  let pico = null;
  celdas.forEach((fila, d) => fila.forEach((n, i) => {
    if (n && (!pico || n > pico.n)) pico = { dia: DIAS_COMPLETOS[d], hora: horas[i], n };
  }));
  const horaPico = total ? horas[indiceDelMayor(porHora)] : null;
  const diaPico = total ? indiceDelMayor(porDia) : null;
  return {
    horas,
    celdas,
    porDia,
    porHora,
    max,
    total,
    pico,
    horaPico: horaPico === null ? null : { hora: horaPico, n: porHora[horas.indexOf(horaPico)] },
    diaPico: diaPico === null ? null : { dia: DIAS_COMPLETOS[diaPico], n: porDia[diaPico] },
    fueraDeHorario: fuera,
    pctFueraDeHorario: total ? Math.round((fuera / total) * 100) : 0,
  };
}

/** "11:00 a 12:00". */
export const franjaDeHora = (h) => `${String(h).padStart(2, "0")}:00 a ${String(h + 1).padStart(2, "0")}:00`;

/** The open work by region, from the offices' backlog: the most differences first. */
export function colaPorRegion(backlog) {
  const regiones = new Map();
  (backlog || []).forEach((o) => {
    const clave = o.region || "Sin región";
    const r = regiones.get(clave) || { region: clave, oficinas: 0, descuadres: 0, revisiones: 0, faltantes: 0, total: 0 };
    r.oficinas += 1;
    r.descuadres += o.descuadres;
    r.revisiones += o.revisiones;
    r.faltantes += o.faltantes;
    r.total += o.total;
    regiones.set(clave, r);
  });
  return [...regiones.values()].sort((a, b) => b.descuadres - a.descuadres || b.total - a.total);
}

/** What an alert's severity means to the team. */
export const TIPO_DE_ALERTA = { alert: "Descuadre", subalert: "Por revisar", blocked: "Falta información" };

/**
 * The workbook "Exportar Excel" writes (`descargarTablas`'s sheets): people, response times, each
 * day, the alerts in and out, the open work by office and each kind of alert. `nombreDeAlerta`
 * names a rule key in the client's words.
 */
export function hojasDelEquipo(datos, { cliente, desde, hasta, nombreDeAlerta = (k) => k }) {
  const subtitulo = `${cliente} · del ${desde} al ${hasta}`;
  const gente = [...datos.people, ...(datos.automatic ? [datos.automatic] : [])];
  return [
    {
      nombre: "Personas",
      titulo: "Trabajo por persona",
      subtitulo,
      columnas: [
        { titulo: "Persona", ancho: 30 }, { titulo: "Correo", ancho: 32 },
        ...TIPOS_DE_TRABAJO.slice(0, 5).map((t) => ({ titulo: t.etiqueta, ancho: 16, formato: "count" })),
        { titulo: "Total", ancho: 10, formato: "count" }, { titulo: "Última actividad", ancho: 18 },
      ],
      filas: gente.map((p) => [
        p === datos.automatic ? "Automático (correo)" : nombreDePersona(p.persona), p === datos.automatic ? "" : p.persona,
        p.dias, p.alertas, p.correcciones, p.documentos, p.avisos, p.acciones, horaBogota(p.ultimaActividad),
      ]),
    },
    {
      nombre: "Tiempos de respuesta",
      titulo: "Tiempo para atender una alerta",
      subtitulo,
      columnas: [
        { titulo: "Persona", ancho: 30 }, { titulo: "Atendidas", ancho: 12, formato: "count" },
        { titulo: "Mediana (h)", ancho: 12 }, { titulo: "Promedio (h)", ancho: 12 },
        ...datos.responses.rangos.map((r) => ({ titulo: r.etiqueta, ancho: 14, formato: "count" })),
      ],
      filas: datos.responses.personas.map((p) => [
        nombreDePersona(p.persona), p.atendidas, p.medianaHoras, p.promedioHoras, ...p.rangos.map((r) => r.n),
      ]),
    },
    {
      nombre: "Días",
      titulo: "Flujo de cada día",
      subtitulo,
      columnas: [
        { titulo: "Día", ancho: 12 }, { titulo: "Procesado por", ancho: 28 }, { titulo: "Hora", ancho: 14 },
        { titulo: "Oficinas leídas", ancho: 14, formato: "count" }, { titulo: "Con error", ancho: 11, formato: "count" },
        { titulo: "Sin filas", ancho: 11, formato: "count" }, { titulo: "Cierres", ancho: 10, formato: "count" },
        { titulo: "Cuadran", ancho: 10, formato: "count" }, { titulo: "Correo del día", ancho: 16 },
        { titulo: "Correos de oficinas", ancho: 16, formato: "count" },
      ],
      filas: datos.days.map((d) => [
        d.dia, d.procesadoPor ? (d.viaCorreo ? "Por correo" : nombreDePersona(d.procesadoPor)) : "Sin procesar", horaBogota(d.fin),
        d.leidas, d.conError, d.sinFilas, d.cierres, d.cuadrados, d.correoDelDia ? horaBogota(d.correoDelDia) : "", d.correosDeOficinas,
      ]),
    },
    {
      nombre: "Entrada y salida",
      titulo: "Alertas levantadas y atendidas por día",
      subtitulo,
      columnas: [
        { titulo: "Día", ancho: 12 }, { titulo: "Descuadres", ancho: 12, formato: "count" },
        { titulo: "Por revisar", ancho: 12, formato: "count" }, { titulo: "Falta información", ancho: 16, formato: "count" },
        { titulo: "Atendidas por el equipo", ancho: 20, formato: "count" }, { titulo: "Resueltas por los datos", ancho: 20, formato: "count" },
      ],
      filas: (datos.flow || []).map((d) => [d.dia, d.descuadres, d.revisiones, d.faltantes, d.atendidas, d.porDatos]),
    },
    {
      nombre: "Cola por oficina",
      titulo: "Trabajo abierto hoy por oficina",
      subtitulo: cliente,
      columnas: [
        { titulo: "Código", ancho: 10 }, { titulo: "Oficina", ancho: 30 }, { titulo: "Región", ancho: 18 },
        { titulo: "Descuadres", ancho: 12, formato: "count" }, { titulo: "Por revisar", ancho: 12, formato: "count" },
        { titulo: "Falta información", ancho: 16, formato: "count" }, { titulo: "Total", ancho: 10, formato: "count" },
        { titulo: "Días de la más antigua", ancho: 18, formato: "count" },
      ],
      filas: (datos.backlog || []).map((o) => [o.officeCode, o.officeName, o.region || "", o.descuadres, o.revisiones, o.faltantes, o.total, o.diasMasAntigua]),
    },
    {
      nombre: "Alertas por tipo",
      titulo: "Cada tipo de alerta levantada en el periodo",
      subtitulo,
      columnas: [
        { titulo: "Alerta", ancho: 34 }, { titulo: "Tipo", ancho: 16 }, { titulo: "Levantadas", ancho: 12, formato: "count" },
        { titulo: "Atendidas", ancho: 12, formato: "count" }, { titulo: "Por los datos", ancho: 14, formato: "count" },
        { titulo: "Abiertas", ancho: 10, formato: "count" }, { titulo: "Mediana de atención (h)", ancho: 20 },
      ],
      filas: (datos.rules || []).map((r) => [
        nombreDeAlerta(r.ruleKey), TIPO_DE_ALERTA[r.severity] || r.severity, r.levantadas, r.atendidas, r.porDatos, r.abiertas, r.medianaHoras,
      ]),
    },
  ];
}
